import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Sparkles, Package, Truck, PartyPopper } from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const RENTAL_REASONS = [
  { icon: Check, title: "Convenienza", body: "Dimenticate di acquistare e immagazzinare grandi quantità di stoviglie usa e getta. Con il noleggio avete esattamente ciò che serve, senza sprechi né complicazioni." },
  { icon: Sparkles, title: "Qualità premium", body: "Stoviglie progettate per resistere alla manipolazione dei bambini e garantire una presentazione impeccabile del cibo e delle bevande." },
  { icon: Package, title: "Variegata selezione", body: "Dai piatti colorati ai bicchieri divertenti, una vasta gamma di opzioni per adattarsi al tema della festa e alle esigenze degli ospiti." },
  { icon: Truck, title: "Ritiro e consegna", body: "Mi occupo personalmente del ritiro e della consegna delle stoviglie direttamente sul luogo dell'evento, per un'organizzazione ancora più semplice." },
];

export default function Feste() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 30]);

  return (
    <div data-testid="feste-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[85vh] pt-32 md:pt-40 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Feste ed eventi · 0–6 anni</p>
          <h1 className="font-display text-[14vw] md:text-[11vw] lg:text-[14rem] leading-[0.82] text-[#3D2140]">
            <RevealText text={["Feste", <span key="1" className="text-[#FF2768]">educative</span>, "su misura."]} stagger={0.14} />
          </h1>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-6 md:col-start-4 font-editorial text-2xl md:text-3xl text-[#3D2140] leading-snug italic">
              Esperienze autentiche per bambini da 0 a 6 anni, costruite sulla relazione, l'ascolto e il rispetto dei tempi di crescita.
            </p>
          </div>
        </div>

        <motion.img
          src={BRAND.logo}
          alt=""
          aria-hidden
          style={{ y: bearY, rotate: bearRotate }}
          className="absolute right-4 md:right-16 top-24 md:top-40 h-32 md:h-52 w-32 md:w-52 rounded-full opacity-80"
        />
      </section>

      {/* SPAZI DEDICATI */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-[#81C3C9]/25" data-testid="spazi-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— In spazi dedicati</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Il posto giusto,<br/><span className="text-[#FF2768]">per la tua festa.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-5 text-lg text-[#3D2140]/85 leading-relaxed">
            <div className="flex items-start gap-4">
              <span className="h-12 w-12 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center shrink-0">
                <MapPin size={20} />
              </span>
              <div>
                <p className="font-display text-3xl text-[#3D2140] mb-2">Spazi privati o sale convenzionate</p>
                <p>Scegliamo insieme la soluzione più adatta alle tue esigenze e al numero di piccoli ospiti. Ogni ambiente viene preparato con cura per accogliere i bambini in sicurezza e libertà.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOLEGGIO STOVIGLIE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="noleggio-section">
        <div className="mb-16 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Una soluzione pratica</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Noleggio<br/><span className="text-[#FF2768]">stoviglie</span><br/>di plastica.
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-lg text-[#3D2140]/80 leading-relaxed font-editorial italic">
              Per rendere ancora più semplice l'organizzazione della tua festa, offro un noleggio di stoviglie di plastica di alta qualità. Piatti, bicchieri, posate — tutto quello che serve per servire i piccoli ospiti in modo comodo e senza stress.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#81C3C9] text-[#81C3C9] text-xs uppercase tracking-[0.3em] font-semibold">
              <PartyPopper size={14} /> Stoviglie riciclabili
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {RENTAL_REASONS.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
                className={`p-8 md:p-10 rounded-3xl border border-[#81C3C9]/40 bg-white hover:border-[#FF2768] transition-colors duration-500 ${i % 2 === 1 ? "md:mt-12" : ""}`}
                data-testid={`rental-reason-${i}`}
              >
                <span className="h-14 w-14 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-6">
                  <Icon size={22} />
                </span>
                <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] mb-4">{r.title}</h3>
                <p className="text-[#3D2140]/75 leading-relaxed">{r.body}</p>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-14 max-w-2xl font-editorial text-2xl text-[#3D2140]/85 italic">
          Aggiungi il noleggio di stoviglie al tuo ordine di organizzazione della festa e goditi una celebrazione senza stress, piena di divertimento.
        </p>
      </section>

      {/* CTA CHIUSURA */}
      <section className="relative px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white overflow-hidden" data-testid="feste-cta-section">
        <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-[#FF2768]/20 blur-3xl pointer-events-none" />
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-[#FFE45C]/15 blur-3xl pointer-events-none" />
        <img src={BRAND.logo} alt="" aria-hidden className="absolute right-6 md:right-16 bottom-6 md:bottom-16 h-32 md:h-48 w-32 md:w-48 rounded-full opacity-60 animate-floaty" />
        <div className="max-w-[1400px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-6">— Un momento speciale</p>
          <h2 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] mb-10 max-w-4xl">
            Scegli di festeggiare<br/>con una <span className="text-[#FF2768]">festa speciale.</span>
          </h2>
          <p className="max-w-2xl text-white/80 text-lg md:text-xl leading-relaxed mb-10 font-editorial italic">
            Se desideri una festa curata, serena e progettata con attenzione, sarò felice di accompagnarti nella realizzazione di un momento speciale per il tuo bambino.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/prenota" data-testid="feste-cta-booking" className="btn-tv-primary">
              Contattami <ArrowRight size={16} />
            </Link>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="feste-cta-whatsapp" className="inline-flex items-center gap-2 px-7 py-[14px] rounded-full border border-white/40 text-white hover:bg-white hover:text-[#3D2140] transition-all">
              Scrivimi su WhatsApp <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
